import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Circle,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { brandPalette } from "../../../../constants/brandPalette";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import {
  createExamRequest,
  editExamRequest,
} from "../../../../modules/admin-modules/exams/Actions";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";
import { getMarkSchemesBySubjectRequest } from "../../../../modules/admin-modules/mark-schemes/Actions";
import {
  closeCreateExamDataModel,
  openQuestionsListModel,
} from "../../../../modules/admin-modules/models/Actions";
import { getAllVideosRequest } from "../../../../modules/admin-modules/videos/Actions";
import StepOne from "../forms/step-one";
import StepTwo from "../forms/step-two";

/** API datetime → value for input[type=datetime-local] */
function apiDateToDatetimeLocal(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/);
  if (m) return `${m[1]}T${m[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return "";
}

/** datetime-local → Laravel-friendly "Y-m-d H:i:s" */
function datetimeLocalToApi(val) {
  if (val == null || String(val).trim() === "") return null;
  const raw = String(val).trim();
  const withSeconds = raw.length === 16 ? `${raw}:00` : raw;
  return withSeconds.replace("T", " ");
}

function getUploadedFile(value) {
  if (value instanceof File) return value;
  if (typeof FileList !== "undefined" && value instanceof FileList) {
    return value[0] || null;
  }
  if (Array.isArray(value) && value[0] instanceof File) return value[0];
  return null;
}

const STEP_ONE_FIELDS = [
  "name",
  "timer",
  "time",
  "published",
  "enable",
  "auto_publish_result",
  "redo_flag",
  "book_mark",
  "type",
  "video_id",
  "pdf",
  "payment_subject_id",
  "start_time",
  "end_time",
  "success_score",
  "difficulty",
];

const STEP_TWO_FIELDS = [
  "random",
  "sub_type",
  "question_number",
  "groups",
  "chapters",
  "exam_tags",
  "mark_scheme_id",
];

const FIELD_LABELS = {
  name: "Assignment title",
  timer: "Timer",
  time: "Timer duration",
  published: "Publishing",
  enable: "Activation",
  auto_publish_result: "Auto-publish results",
  redo_flag: "Allow redo",
  book_mark: "Importance",
  type: "Assignment type",
  video_id: "Video",
  pdf: "PDF file",
  payment_subject_id: "Payment subject",
  random: "Question selection",
  sub_type: "Scope",
  question_number: "Number of questions",
  groups: "Groups",
  chapters: "Chapters",
  exam_tags: "Tags",
  mark_scheme_id: "Mark scheme",
};

function errorKeys(errors) {
  return Object.keys(errors || {});
}

function isStepOneField(key) {
  return STEP_ONE_FIELDS.includes(key);
}

function isStepTwoField(key) {
  return (
    STEP_TWO_FIELDS.includes(key) ||
    String(key).startsWith("chapters") ||
    String(key).startsWith("exam_tags")
  );
}

function hasStepOneErrors(errors) {
  return errorKeys(errors).some(isStepOneField);
}

function hasStepTwoErrors(errors) {
  return errorKeys(errors).some(isStepTwoField);
}

function missingFieldLabels(errors) {
  return errorKeys(errors).map((key) => FIELD_LABELS[key] || key);
}

const FIELD_SCROLL_ORDER = [...STEP_ONE_FIELDS, ...STEP_TWO_FIELDS];

function firstErrorKey(errors) {
  const keys = errorKeys(errors);
  return FIELD_SCROLL_ORDER.find((key) => keys.includes(key)) || keys[0];
}

function scrollToExamField(fieldKey) {
  if (typeof document === "undefined") return;
  const root =
    document.querySelector(".chakra-modal__content") || document.body;
  const selectors = fieldKey
    ? [
        `[data-exam-field="${fieldKey}"]`,
        `[name="${fieldKey}"]`,
        `#${fieldKey}`,
      ]
    : [];
  const el =
    selectors
      .map((selector) => root.querySelector(selector))
      .find(Boolean) ||
    root.querySelector("[aria-invalid='true']") ||
    root.querySelector("#exam-form-errors-footer");
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function FormErrorsAlert({
  missingLabels,
  stepOneHasErrors,
  stepTwoHasErrors,
  isFormOne,
  onGoToStepOne,
  onGoToStepTwo,
  id,
}) {
  if (!missingLabels.length) return null;
  return (
    <Alert
      id={id}
      status="error"
      borderRadius="md"
      mb={4}
      alignItems="flex-start"
    >
      <AlertIcon />
      <Box>
        <AlertTitle fontSize="sm">
          Please complete the required fields
        </AlertTitle>
        <AlertDescription fontSize="sm">
          Missing: {missingLabels.join(", ")}
          {stepOneHasErrors && !isFormOne ? (
            <Button
              type="button"
              variant="link"
              colorScheme="red"
              ml={2}
              onClick={onGoToStepOne}
            >
              Go to step 1
            </Button>
          ) : null}
          {stepTwoHasErrors && isFormOne ? (
            <Button
              type="button"
              variant="link"
              colorScheme="red"
              ml={2}
              onClick={onGoToStepTwo}
            >
              Go to step 2
            </Button>
          ) : null}
        </AlertDescription>
      </Box>
    </Alert>
  );
}

const AddExamData = ({ isAdd, data, setSelectedData, setExamData }) => {
  const [isFormOne, setFormOne] = useState(true);

  const openExamData = useSelector((state) => state.model.isOpen15);
  const chapters = useSelector((state) => state.chapters.chapters);
  const groups = useSelector((state) => state.groups.groups);
  const isSaving = useSelector((state) => state.exams.isLoading);

  const dispatch = useDispatch();
  useEffect(() => {
    if (openExamData) {
      if (chapters?.length == 0) dispatch(getAllChaptersRequest());
      if (groups?.length == 0) dispatch(getAllGroupsRequest());
      // dispatch(getPerformanceGroupsRequest());
      dispatch(getAllVideosRequest());
      dispatch(getMarkSchemesBySubjectRequest());
    }
  }, [openExamData]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
    trigger,
    getFieldState,
  } = useForm({
    defaultValues: {
      time: "0",
      published: "0",
      enable: "0",
      auto_publish_result: "0",
      redo_flag: "0",
      book_mark: "0",
      type: "1",
      difficulty: "0",
    },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (!openExamData) return;

    setFormOne(true);

    if (!isAdd && data?.id) {
      reset({
        ...data,
        time: data?.time ?? "0",
        question_number: data?.question_number,
        start_time: apiDateToDatetimeLocal(data?.start_time),
        end_time: apiDateToDatetimeLocal(data?.end_time),
        mark_scheme_id: data?.mark_scheme_id ?? data?.mark_scheme?.id ?? "",
      });

      const groupIds = (data?.groups || []).map((g) => g?.id ?? g);
      setValue("groups", groupIds);
      setValue(
        "mark_scheme_id",
        data?.mark_scheme_id ?? data?.mark_scheme?.id ?? "",
        { shouldValidate: false }
      );

      (data?.chapters || []).forEach((ch, idx) => {
        const i = idx + 1;
        setValue(`chapters[${i}][chapter_id]`, ch?.id ?? ch?.chapter_id);
        setValue(
          `chapters[${i}][no_of_questions]`,
          ch?.pivot?.no_of_questions ?? ch?.no_of_questions ?? 0
        );
      });

      (data?.exam_tags || []).forEach((tag, idx) => {
        const i = idx + 1;
        setValue(
          `exam_tags[${i}][question_tag_id]`,
          tag?.question_tag_id ?? tag?.tag_id ?? tag?.id
        );
        setValue(
          `exam_tags[${i}][question_number]`,
          tag?.question_number ?? tag?.no_of_questions ?? 0
        );
      });
    } else {
      reset({
        time: "0",
        published: "0",
        enable: "0",
        auto_publish_result: "0",
        redo_flag: "0",
        book_mark: "0",
        type: "1",
        difficulty: "0",
        mark_scheme_id: "",
      });
    }
  }, [openExamData, isAdd, data?.id, reset, setValue]);

  const watchSubType = watch("sub_type") || data?.sub_type;
  const watchTotalQustions = watch("question_number") || data?.question_number;
  const watchChapters = watch("chapters") || data?.chapters;
  const watchExamTags = watch("exam_tags") || data?.exam_tags;
  const watchTimer = watch("timer") || data?.timer;
  const watchQuestionWay = watch("random") || data?.random;
  const watchType = watch("type") || data?.type;

  const stepOneFieldsToValidate = useMemo(() => {
    const fields = [
      "name",
      "timer",
      "published",
      "enable",
      "auto_publish_result",
      "redo_flag",
      "book_mark",
      "type",
    ];
    if (String(watchType) === "6") fields.push("video_id");
    if (String(watchTimer) === "1") fields.push("time");
    return fields;
  }, [watchType, watchTimer]);

  const stepOneHasErrors = hasStepOneErrors(errors);
  const stepTwoHasErrors = hasStepTwoErrors(errors);
  const missingLabels = missingFieldLabels(errors);

  const goToStepOne = () => setFormOne(true);

  const revealFirstError = (formErrors) => {
    const key = firstErrorKey(formErrors);
    window.setTimeout(() => scrollToExamField(key), 80);
  };

  const goToStepTwo = async () => {
    const isValid = await trigger(stepOneFieldsToValidate);
    if (isValid) {
      setFormOne(false);
      return;
    }
    const firstKey = stepOneFieldsToValidate.find(
      (key) => getFieldState(key).invalid
    );
    window.setTimeout(() => scrollToExamField(firstKey), 80);
  };

  const onInvalid = (formErrors) => {
    if (hasStepOneErrors(formErrors)) {
      setFormOne(true);
    } else {
      setFormOne(false);
    }
    revealFirstError(formErrors);
  };

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color={brandPalette.primary}
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add Assignment" : "Edit Assignment"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Two steps: assignment settings, then question selection rules
      </Text>
    </Box>
  );

  const onSubmit = async (values) => {
    const totel = {
      ...data,
      ...values,
      start_time: datetimeLocalToApi(values?.start_time),
      end_time: datetimeLocalToApi(values?.end_time),
    };

    let formData = new FormData();
    let chapters = [];
    for (let key in totel) {
      if (key == "groups") {
        totel[key]?.map((val, idx) =>
          formData.append(`groups[${idx}]`, val?.id ? val?.id : val)
        );
      } else if (key == "chapters") {
        let primaryChapterSet = false;
        totel[key]?.map((val, idx) => {
          if (!val) return;
          if (val) chapters.push(val);
          const isPrimary =
            Number(totel.sub_type) === 2 && !primaryChapterSet;
          if (isPrimary) primaryChapterSet = true;
          formData.append(
            `chapters[${idx}][chapter_id]`,
            val.id ? val.id : val?.chapter_id
          );
          formData.append(
            `chapters[${idx}][no_of_questions]`,
            Number(totel.sub_type) === 4 || isPrimary
              ? 0
              : val.no_of_questions ??
                val.pivot?.no_of_questions ??
                totel?.question_number
          );
        });
      } else if (key == "exam_tags" && Number(totel.sub_type) === 4) {
        totel[key]?.map((val, idx) => {
          if (!val) return;
          formData.append(
            `exam_tags[${idx}][question_tag_id]`,
            val.question_tag_id ? val.question_tag_id : val?.tag_id ?? val?.id
          );
          formData.append(
            `exam_tags[${idx}][question_number]`,
            val.question_number
              ? val.question_number
              : val.no_of_questions ?? totel?.question_number
          );
        });
      } else if (key === "start_time") {
        if (totel.start_time != null) {
          formData.append("start_time", totel.start_time);
        }
      } else if (key === "end_time") {
        if (totel.end_time != null) {
          formData.append("end_time", totel.end_time);
        }
      } else if (key === "pdf") {
        const file = getUploadedFile(totel.pdf);
        if (file) formData.append("pdf", file);
      } else if (key === "mark_scheme_id") {
        if (totel.mark_scheme_id != null && totel.mark_scheme_id !== "") {
          formData.append("mark_scheme_id", totel.mark_scheme_id);
        }
      } else if (key !== "exam_tags" && key !== "mark_scheme" && totel[key]) {
        formData.append(key, totel[key]);
      }
    }
    if (totel["timer"] == 0) {
      formData.append("time", 0);
    }
    const action = (data) => {
      dispatch(closeCreateExamDataModel());
      setSelectedData({});
      reset();
      setFormOne(true);

      const containerChapterIds = (chapters || [])
        .slice(1)
        .map((ch) => ch?.chapter_id ?? ch?.id)
        .filter((id) => id != null && id !== "");

      setExamData({
        chapter_id: containerChapterIds[0],
        chapter_ids: containerChapterIds,
        exam: data?.id,
        question_number: data?.question_number,
      });
      isAdd && watchQuestionWay == 0 && dispatch(openQuestionsListModel());
    };
    if (isAdd) {
      dispatch(createExamRequest(formData, action));
    } else {
      formData.append("_method", "PUT");

      dispatch(
        editExamRequest({
          id: data?.id,
          formData: formData,
          action: action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeCreateExamDataModel());
    setSelectedData({});
    reset();
    setFormOne(true);
  };

  useEffect(() => {
    if (watchQuestionWay == 0) setValue("sub_type", 2);
  }, [watchQuestionWay]);
  return (
    <MainModel
      isLoading={isSaving}
      header={header}
      closeFunc={closeFunc}
      openVar={openExamData}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit, onInvalid)}
      size="5xl"
      contentProps={{ ...adminModalContentProps, overflow: "visible" }}
      headerProps={adminModalHeaderProps}
      bodyProps={{ ...adminModalBodyProps, overflow: "visible" }}
      hideDefaultFooter
    >
      <Box position="relative">
        {(isSaving) && (
          <Flex
            position="absolute"
            inset={0}
            bg="blackAlpha.400"
            zIndex={4}
            align="center"
            justify="center"
            borderRadius="md"
          >
            <VStack spacing={3} bg="var(--surface-raised)" px={6} py={5} borderRadius="lg" boxShadow="lg">
              <Spinner
                size="lg"
                color={brandPalette.primary}
                thickness="3px"
              />
              <Text fontWeight="700" color="var(--text-primary)">
                Saving assignment...
              </Text>
            </VStack>
          </Flex>
        )}
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <HStack dir="ltr" w="100%" justifyContent="center" spacing="50px" mb={6}>
          <HStack onClick={goToStepOne} cursor="pointer">
            <Circle
              size="40px"
              bg={
                stepOneHasErrors
                  ? "red.500"
                  : isFormOne
                    ? brandPalette.primary
                    : "var(--surface-muted)"
              }
              color={
                stepOneHasErrors || isFormOne ? "white" : brandPalette.primary
              }
              borderColor={stepOneHasErrors ? "red.500" : brandPalette.primary}
              border="1px solid"
            >
              1
            </Circle>
            <Text fontWeight="700" color={stepOneHasErrors ? "red.500" : "var(--text-primary)"}>
              Assignment details
            </Text>
          </HStack>
          <HStack onClick={goToStepTwo} cursor="pointer">
            <Circle
              size="40px"
              bg={
                stepTwoHasErrors
                  ? "red.500"
                  : !isFormOne
                    ? brandPalette.primary
                    : "var(--surface-muted)"
              }
              color={
                stepTwoHasErrors || !isFormOne ? "white" : brandPalette.primary
              }
              borderColor={stepTwoHasErrors ? "red.500" : brandPalette.primary}
              border="1px solid"
            >
              2
            </Circle>
            <Text fontWeight="700" color={stepTwoHasErrors ? "red.500" : "var(--text-primary)"}>
              Questions
            </Text>
          </HStack>
        </HStack>

        {missingLabels.length > 0 && (
          <FormErrorsAlert
            missingLabels={missingLabels}
            stepOneHasErrors={stepOneHasErrors}
            stepTwoHasErrors={stepTwoHasErrors}
            isFormOne={isFormOne}
            onGoToStepOne={goToStepOne}
            onGoToStepTwo={() => setFormOne(false)}
          />
        )}

        {/* first form */}
        <StepOne
          key={data?.id ? `exam-step1-${data.id}` : "exam-step1-new"}
          data={data}
          register={register}
          watchTimer={watchTimer}
          errors={errors}
          isFormOne={isFormOne}
          watchType={watchType}
          setValue={setValue}
          isAdd={isAdd}
        />
        {/* form two */}
        <StepTwo
          key={data?.id ? `exam-step2-${data.id}` : "exam-step2-new"}
          watchSubType={watchSubType}
          watchTotalQustions={watchTotalQustions}
          watchQuestionWay={watchQuestionWay}
          data={data}
          setValue={setValue}
          register={register}
          errors={errors}
          isFormOne={isFormOne}
          watchChapters={watchChapters}
          watchExamTags={watchExamTags}
          isAdd={isAdd}
        />
        <Flex
          mt={8}
          flexDir="column"
          align="stretch"
          gap={4}
        >
          <FormErrorsAlert
            id="exam-form-errors-footer"
            missingLabels={missingLabels}
            stepOneHasErrors={stepOneHasErrors}
            stepTwoHasErrors={stepTwoHasErrors}
            isFormOne={isFormOne}
            onGoToStepOne={goToStepOne}
            onGoToStepTwo={() => setFormOne(false)}
          />
          <Flex
            flexDir={{ base: "column", sm: "row" }}
            align={{ base: "stretch", sm: "center" }}
            justify="space-between"
            gap={4}
          >
          <Button
            type="button"
            onClick={isFormOne ? goToStepTwo : goToStepOne}
            color="white"
            bg={brandPalette.primary}
            _focus={{ outline: "none" }}
            minW="200px"
            borderRadius="lg"
            fontWeight="800"
            isDisabled={isSaving}
            _hover={{ bg: brandPalette.accentDark }}
          >
            {isFormOne ? "Next step" : "Previous step"}
          </Button>
          <Flex justify="flex-end" gap={6} flexWrap="wrap">
            <Button
              type="button"
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              isDisabled={isSaving}
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isSaving}
              loadingText="Saving"
              isDisabled={isSaving}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
          </Flex>
        </Flex>
      </form>
      </Box>
    </MainModel>
  );
};

export default AddExamData;
